import React, { useEffect, useState, useCallback } from "react";
import { MDBBtn, MDBContainer } from "mdb-react-ui-kit";
import { useAuth } from "../../components/Auth";
import { ReferenceAnalysisReviewModal } from "./components";
import {
  convertISOToReadableDate,
  getReferenceAnalysisReports,
  getReferenceAnalysisReportByPersistentId,
  showErrorToast,
  extractUniqueScreeningOutcomeTypes,
} from "../../helpers";
import { getDataTableColumns } from "./columns";
import ErrorMessage from "../../components/ErrorMessage/ErrorMessage";
import { CountCard } from "../../components";
import { FilterIconHeader } from "../../components/FilterIconHeader";
import { SwitchForm } from "../../components/SwitchForm";
import { referenceSignals } from "../../constants";
import { exportReferenceAnalysisToExcel } from "./export";
import { DataTable } from "stm-frontend-library";
import { getSelectedJournalIds } from "../../helpers/journals";

const ReferenceAnalysis = () => {
  const [error, setError] = useState(false);

  const { profile } = useAuth();
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [fullManuscriptsById, setFullManuscriptsById] = useState({});

  const [totalHits, setTotalHits] = useState(0);
  const [selectedDataRowIndex, setSelectedDataRowIndex] = useState(null);
  const [filterSelection, setFilterSelection] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const typeLabels = referenceSignals.reduce((acc, current) => {
    acc[current.value] = current.label;
    return acc;
  }, {});

  const getNumericPart = (outcome) => {
    const { type, items_summary, items, supplementary_data } = outcome;
    if (!typeLabels[type]) return "";

    let itemsCount;
    if (items_summary) {
      itemsCount = type === "doiAnalysis"
        ? items_summary.counts?.by_group?.invalid || 0
        : items_summary.counts?.total || 0;
    } else if (Array.isArray(items)) {
      itemsCount = items.length;
    } else {
      return "";
    }

    const parts = [
      String(itemsCount),
      supplementary_data?.retrievedReferences !== undefined &&
        `/${supplementary_data.retrievedReferences}`,
      supplementary_data?.totalReferences !== undefined &&
        ` (${supplementary_data.totalReferences})`,
      type === "doiAnalysis" &&
        supplementary_data?.unidentifiedPercent > 0 &&
        ` — ${Math.round(supplementary_data.unidentifiedPercent)}%`,
    ]
      .filter(Boolean)
      .join("");

    return parts;
  };

  const mergeDefined = (base, patch) => {
    const out = { ...base };
    Object.entries(patch || {}).forEach(([k, v]) => {
      if (v !== undefined) out[k] = v;
    });
    return out;
  };

  const upsertFullManuscript = useCallback((persistentId, fullManuscript) => {
    if (!persistentId || !fullManuscript) return;

    setFullManuscriptsById((prev) => {
      if (prev[persistentId]) return prev;
      return { ...prev, [persistentId]: fullManuscript };
    });

    setAllData((prev) =>
      prev.map((m) =>
        m.persistent_id === persistentId ? mergeDefined(m, fullManuscript) : m
      )
    );
  }, []);

  const handleRowClick = useCallback(
    async (index) => {
      const manuscript = filteredData[index];
      if (!manuscript) return;

      const persistentId = manuscript.persistent_id;
      const cachedFull = persistentId ? fullManuscriptsById[persistentId] : null;

      if (cachedFull) {
        setSelectedDataRowIndex(index);
        setModalData(cachedFull);
        return;
      }

      const screeningOutcomes = manuscript.screening_outcomes || [];
      const hasTruncated = screeningOutcomes.some(
        (outcome) => outcome.truncated === true
      );

      if (hasTruncated) {
        setIsModalLoading(true);
        setSelectedDataRowIndex(index);

        try {
          const { data } = await getReferenceAnalysisReportByPersistentId(
            persistentId
          );
          const fullManuscript = data?.manuscripts?.[0];

          if (fullManuscript) {
            upsertFullManuscript(persistentId, fullManuscript);
            setModalData(fullManuscript);
          } else {
            setModalData(manuscript);
          }
        } catch (err) {
          console.error("Failed to fetch full manuscript data:", err);
          showErrorToast();
          setModalData(manuscript);
        } finally {
          setIsModalLoading(false);
        }
      } else {
        setSelectedDataRowIndex(index);
        setModalData(manuscript);
      }
    },
    [filteredData, fullManuscriptsById, upsertFullManuscript]
  );

  const handleCloseModal = () => {
    setSelectedDataRowIndex(null);
    setModalData(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setError(false);
        setIsLoading(true);
        const selectedJournalIds = await getSelectedJournalIds();
        const {
          data: { manuscripts = [], total = 0 } = {},
        } = await getReferenceAnalysisReports(selectedJournalIds, true);

        setAllData(manuscripts);
        setTotalHits(total);

        const uniqueTypes = extractUniqueScreeningOutcomeTypes(manuscripts);
        const initialFilter = Object.fromEntries(
          uniqueTypes.map((type) => [type, true])
        );
        setFilterSelection(initialFilter);
      } catch (error) {
        showErrorToast();
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (Object.keys(filterSelection).length) {
      const filteredManuscripts = allData.filter((manuscript) =>
        manuscript.screening_outcomes.some(
          (outcome) => filterSelection[outcome.type]
        )
      );
      setFilteredData(filteredManuscripts);
    }
  }, [filterSelection, allData]);

  const options = referenceSignals.filter(
    (signal) => profile[`tool.${signal.value}`]
  );

  const handleFilterSubmit = (values) => {
    setFilterSelection(values);
  };

  const handleFilterReset = () => {
    const resetValues = Object.fromEntries(
      Object.keys(filterSelection).map((type) => [type, true])
    );
    setFilterSelection(resetValues);
  };

  const filterComponent = (
    <FilterIconHeader label="Hits" dropdownTitle="Signal Type Selection">
      <SwitchForm
        handleSubmit={handleFilterSubmit}
        handleReset={handleFilterReset}
        initialValues={filterSelection}
        options={options}
      />
    </FilterIconHeader>
  );

  const tableColumns = getDataTableColumns(filterComponent);

  const dataWithActions = filteredData?.map((element, index) => {
    const screeningOutcomes = element.screening_outcomes || [];

    const rawMatchStatus = screeningOutcomes
      .map((outcome) => {
        const label = typeLabels[outcome.type];
        const numbers = getNumericPart(outcome);
        return label && numbers ? `${label} ${numbers}` : "";
      })
      .filter(Boolean)
      .join("; ");

    const matchStatusElements = screeningOutcomes.map((outcome, idx) => {
      const label = typeLabels[outcome.type];
      const numbers = getNumericPart(outcome);

      if (!label || !numbers) return null;

      return (
        <React.Fragment key={idx}>
          <small>
            <span className="text-primary">{label}</span>{" "}
            <span className="text-nowrap">{numbers}</span>
          </small>
          <br />
        </React.Fragment>
      );
    });

    return {
      ...element,
      timestamp: convertISOToReadableDate(element.timestamp, "DD MMM YYYY"),
      match_status: <>{matchStatusElements}</>,
      raw_match_status: rawMatchStatus,
      clickEvent: () => handleRowClick(index),
    };
  });

  const handleExport = () => {
    exportReferenceAnalysisToExcel(dataWithActions, "ReferenceAnalysis");
  };

  return (
    <MDBContainer className="my-5">
      {selectedDataRowIndex !== null && (
        <ReferenceAnalysisReviewModal
          contentData={modalData}
          onClose={handleCloseModal}
          isLoading={isModalLoading}
        />
      )}
      {error && <ErrorMessage />}
      {!error && (
        <>
          <div className="d-flex justify-content-center m-5">
            <CountCard
              count={totalHits}
              title={"ANALYSIS SIGNALS"}
              subtitle={"Screened manuscripts with hits"}
            />
          </div>
          <div className="d-flex justify-content-end mb-3">
            <MDBBtn
              disabled={isLoading}
              color="primary"
              className="px-4"
              onClick={handleExport}
            >
              Export
            </MDBBtn>
          </div>
          <DataTable
            columns={tableColumns}
            rows={dataWithActions}
            isLoading={isLoading}
          />
        </>
      )}
    </MDBContainer>
  );
};

export default ReferenceAnalysis;
